import React, {useEffect} from 'react';
import {Modal, View, Text, StyleSheet, Animated} from 'react-native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, typography} from '~constants/design';
import Ionicons from 'react-native-vector-icons/Ionicons';
import LottieView from 'lottie-react-native';

type Props = {
  visible: boolean;
  onClose?: () => void;
  autoCloseMs?: number;
  lottieSource?: any;
  formType?: string;
  title?: string;
  description?: string;
};

const QuizSuccessModal: React.FC<Props> = ({
  visible,
  onClose,
  autoCloseMs = 2200,
  lottieSource,
  formType,
  title,
  description,
}) => {
  const {colors} = useTheme();
  const scale = React.useRef(new Animated.Value(0.6)).current;
  const opacity = React.useRef(new Animated.Value(0)).current;
  const propsHasLottie = !!lottieSource;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(scale, {toValue: 1, duration: 450, useNativeDriver: true}),
        Animated.timing(opacity, {toValue: 1, duration: 350, useNativeDriver: true}),
      ]).start();

      // If no lottieSource provided, fallback to timeout auto-close
      if (!propsHasLottie) {
        const t = setTimeout(() => {
          onClose && onClose();
        }, autoCloseMs);
        return () => clearTimeout(t);
      }
    } else {
      Animated.parallel([
        Animated.timing(scale, {toValue: 0.7, duration: 200, useNativeDriver: true}),
        Animated.timing(opacity, {toValue: 0, duration: 200, useNativeDriver: true}),
      ]).start();
    }
  }, [visible, autoCloseMs, onClose, opacity, scale, propsHasLottie]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    fullScreen: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    title: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: '700',
      color: '#000',
      marginTop: spacing.md,
      textAlign: 'center',
    },
    desc: {
      color: '#000',
      marginTop: spacing.sm,
      textAlign: 'center',
      fontSize: typography.fontSize.base,
      paddingHorizontal: spacing.lg,
    },
    checkWrapper: {
      width: 160,
      height: 160,
      borderRadius: 80,
      backgroundColor: 'rgba(255,255,255,0.12)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    subtleCircle: {
      position: 'absolute',
      width: 300,
      height: 300,
      borderRadius: 150,
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  });

  return (
    <Modal visible={visible} transparent animationType="none" statusBarTranslucent onRequestClose={onClose}>
      <Animated.View
        style={{
          flex: 1,
          backgroundColor: colors.background,
          transform: [{scale: opacity.interpolate({inputRange: [0, 1], outputRange: [0.98, 1]})}],
          opacity: opacity,
        }}>
        <View style={styles.fullScreen}>
          <Animated.View
            style={[styles.subtleCircle, {opacity: opacity.interpolate({inputRange: [0, 1], outputRange: [0, 0.5]})}]}
          />
          <Animated.View style={{transform: [{scale}]}}>
            {propsHasLottie ? (
              <View style={{width: 260, height: 260, justifyContent: 'center', alignItems: 'center'}}>
                <LottieView
                  source={lottieSource}
                  autoPlay
                  loop={false}
                  style={{width: 260, height: 260}}
                  onAnimationFinish={() => {
                    // Close modal once Lottie completes
                    setTimeout(() => {
                      onClose && onClose();
                    }, 1000);
                  }}
                />
              </View>
            ) : (
              <View style={styles.checkWrapper}>
                <Ionicons name="checkmark" size={78} color="#fff" />
              </View>
            )}
          </Animated.View>

          <Text style={styles.title}>{title || 'All set!'}</Text>
          <Text style={styles.desc}>
            {description || (
              <>
                You have completed{' '}
                {formType == 'reset_table'
                  ? 'The Reset Table'
                  : formType == 'cultural_edit'
                  ? 'The Cultural Edit'
                  : ''}{' '}
                quiz. Redirecting…
              </>
            )}
          </Text>
        </View>
      </Animated.View>
    </Modal>
  );
};

export default QuizSuccessModal;
